import * as React from "react";
import {Box, Checkbox, TableSortLabel, Tooltip, Typography} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import {useTranslation} from "react-i18next";
import {RecipientCandidateSort, RequestRecipientCandidateDTO} from "@/api/data-contracts";
import {AdminI18N, RequestI18N} from "@/translations";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {DataTable} from "@/lib/components/DataTable";
import {areAllDisplayedSelected, nextRecipientSort} from "@/common/request-details/recipient-selection-logic";

interface RecipientCandidateListProps {
    candidates: RequestRecipientCandidateDTO[];
    selected: ReadonlySet<number>;
    totalElements: number;
    sort: RecipientCandidateSort;
    onSortChange: (sort: RecipientCandidateSort) => void;
    onToggle: (companyId: number) => void;
    onBulkToggle: () => void;
}

/**
 * Row shape rendered by `DataTable`: `companyId` is optional on the generated DTO, so candidates without
 * one are dropped while building this array. That keeps `id` (DataTable's row identity, read via
 * `row.original.id`) and the `selected` set free of `undefined`, which the raw DTO could otherwise carry
 * into both the React key and the selection state.
 */
interface RecipientCandidateRow {
    id: number;
    companyName: string;
    distanceKm?: number;
}

/**
 * Candidate list of the "Empfänger der Anfrage festlegen" dialog: bulk-action / count header row, then
 * `DataTable` with a checkbox column and sortable "Unternehmen" / "Entfernung" headers. `DataTable` has
 * no built-in multi-select or sortable header, but it renders `columnDef.header` and `columnDef.cell`
 * through `flexRender`, so both are supplied via `columns` without changing `DataTable` itself. Its
 * single-row-selection wiring (`selectedRowId`, `onRowClick`) stays unused here and therefore inert: with
 * neither prop passed, a row click never fires a callback and `selectedRowId === row.original.id` is
 * always false. Sorting is server-side, this component only reports the desired
 * {@link RecipientCandidateSort} value to the parent (see `RecipientSelectionDialog.tsx`, which owns the
 * fetch).
 *
 * Distance rendering follows the request overview's plain "<km> km" convention
 * (`RequestOverviewTable.tsx`); unlike the bidder-facing overview there is no hourglass/warning icon here,
 * every value-less case (no record, PENDING, ERROR) is shown as the same `distanceUnknown` dash.
 */
const RecipientCandidateList: React.FC<RecipientCandidateListProps> = ({
    candidates,
    selected,
    totalElements,
    sort,
    onSortChange,
    onToggle,
    onBulkToggle
}) => {
    const { t } = useTranslation();

    const rows = React.useMemo<RecipientCandidateRow[]>(
        () =>
            candidates
                .filter((candidate): candidate is RequestRecipientCandidateDTO & { companyId: number } =>
                    candidate.companyId != null
                )
                .map((candidate) => ({
                    id: candidate.companyId,
                    companyName: candidate.companyName ?? "",
                    distanceKm: candidate.distanceKm ?? undefined
                })),
        [candidates]
    );

    const displayedCompanyIds = React.useMemo(() => rows.map((row) => row.id), [rows]);
    const isAllDisplayedSelected = areAllDisplayedSelected(selected, displayedCompanyIds);

    const nameDirection = sort === RecipientCandidateSort.NAME_DESC ? "desc" : "asc";
    const distanceDirection = sort === RecipientCandidateSort.DISTANCE_DESC ? "desc" : "asc";
    const isNameActive = sort === RecipientCandidateSort.NAME_ASC || sort === RecipientCandidateSort.NAME_DESC;
    const isDistanceActive =
        sort === RecipientCandidateSort.DISTANCE_ASC || sort === RecipientCandidateSort.DISTANCE_DESC;

    // The switching rule itself (which column starts ascending, which one flips) lives in
    // nextRecipientSort (recipient-selection-logic.ts) and is covered there by Vitest.
    const toggleNameSort = React.useCallback(
        () => onSortChange(nextRecipientSort(sort, "NAME")),
        [onSortChange, sort]
    );
    const toggleDistanceSort = React.useCallback(
        () => onSortChange(nextRecipientSort(sort, "DISTANCE")),
        [onSortChange, sort]
    );

    const columns = React.useMemo<ColumnDef<RecipientCandidateRow>[]>(
        () => [
            {
                id: "select",
                header: "",
                size: 48,
                cell: (info) => (
                    <Checkbox
                        checked={selected.has(info.row.original.id)}
                        onChange={() => onToggle(info.row.original.id)}
                        inputProps={{ "aria-label": info.row.original.companyName }}
                    />
                )
            },
            {
                id: "companyName",
                accessorKey: "companyName",
                header: () => (
                    <TableSortLabel active={isNameActive} direction={nameDirection} onClick={toggleNameSort}>
                        {t(RequestI18N.details.recipientSelection.columnCompany)}
                    </TableSortLabel>
                ),
                cell: (info) => (
                    <span data-cy="recipient-company-name">{info.row.original.companyName}</span>
                )
            },
            {
                id: "distanceKm",
                accessorKey: "distanceKm",
                header: () => (
                    <Tooltip title={t(RequestI18N.details.recipientSelection.distanceHint)}>
                        <TableSortLabel
                            active={isDistanceActive}
                            direction={distanceDirection}
                            onClick={toggleDistanceSort}
                        >
                            {t(RequestI18N.details.recipientSelection.columnDistance)}
                        </TableSortLabel>
                    </Tooltip>
                ),
                cell: (info) => (
                    <span data-cy="recipient-company-distance">
                        {info.row.original.distanceKm != null
                            ? `${info.row.original.distanceKm} km`
                            : t(RequestI18N.details.recipientSelection.distanceUnknown)}
                    </span>
                )
            }
        ],
        [selected, onToggle, isNameActive, nameDirection, toggleNameSort, isDistanceActive, distanceDirection, toggleDistanceSort, t]
    );

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <ButtonEx
                    data-cy="recipient-bulk-toggle"
                    size="small"
                    label={t(
                        isAllDisplayedSelected
                            ? RequestI18N.details.recipientSelection.deselectAll
                            : RequestI18N.details.recipientSelection.selectAll
                    )}
                    onClick={onBulkToggle}
                />
                <Typography variant="body2" data-cy="recipient-count-label">
                    {t(RequestI18N.details.recipientSelection.countLabel, {
                        selected: selected.size,
                        total: totalElements
                    })}
                </Typography>
            </Box>
            <DataTable columns={columns} data={rows} noResultsValue={t(AdminI18N.companies.message.notFound)} maxHeight={320} />
        </Box>
    );
};

export default RecipientCandidateList;
